import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  DirectionsCar,
  People,
  Assignment,
  CalendarToday,
  CheckCircle,
  Pending,
  Cancel,
  MonetizationOn,
} from '@mui/icons-material';

// ✅ Base API URL (Dynamic from env)
import { API_BASE_URL } from '../services/api';
import { apiFetch } from '../services/apiFetch'; 

interface DashboardSummary {
  total_cars: number;
  total_customers: number;
  total_bookings: number;
  todays_bookings: number;
  completed_bookings: number;
  pending_bookings: number;
  total_earnings: number;
}

interface Booking {
  booking_id: number;
  booking_number: number;
  contact_person_name: string;
  total_amount: number;
  booking_date: string;
  status: string;
}

const Dashboard: React.FC = () => {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingBookings, setLoadingBookings] = useState<boolean>(true);

  // Fetch Dashboard Summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/dashboard/summary`);
        const result = await res.json();
        if (result.status === 'success' && result.data) {
          setSummary(result.data);
        }
      } catch (error) {
        console.error('Error fetching summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  // Fetch Recent Bookings (Top 5)
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/bookings/dashboard`);
        const result = await res.json();
        if (result.status === 'success' && result.data?.bookings) {
          const sorted = result.data.bookings
            .sort(
              (a: Booking, b: Booking) =>
                new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime()
            )
            .slice(0, 5);
          setRecentBookings(sorted);
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoadingBookings(false);
      }
    };
    fetchBookings();
  }, []);

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Chip icon={<CheckCircle />} label="Completed" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<Pending />} label="Pending" color="warning" size="small" />;
      case 'cancelled':
        return <Chip icon={<Cancel />} label="Cancelled" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const summaryCards = summary
    ? [
        {
          title: 'Total Cars',
          value: summary.total_cars,
          icon: <DirectionsCar sx={{ fontSize: 38, color: 'primary.main' }} />,
        },
        {
          title: 'Customers',
          value: summary.total_customers,
          icon: <People sx={{ fontSize: 38, color: 'success.main' }} />,
        },
        {
          title: 'Total Bookings',
          value: summary.total_bookings,
          icon: <Assignment sx={{ fontSize: 38, color: 'warning.main' }} />,
        },
        {
          title: "Today's Bookings",
          value: summary.todays_bookings,
          icon: <CalendarToday sx={{ fontSize: 38, color: 'secondary.main' }} />,
        },
        {
          title: 'Completed Bookings',
          value: summary.completed_bookings,
          icon: <CheckCircle sx={{ fontSize: 38, color: 'info.main' }} />,
        },
        {
          title: 'Pending Bookings',
          value: summary.pending_bookings,
          icon: <Pending sx={{ fontSize: 38, color: 'warning.main' }} />,
        },
        {
          title: 'Total Earnings',
          value: `${process.env.REACT_APP_SITE_CURRENCY} ${summary.total_earnings}`,
          icon: <MonetizationOn sx={{ fontSize: 38, color: 'error.main' }} />,
        },
      ]
    : [];

  return (
    <Box p={3}>
      <Typography variant="h4"  fontWeight="bold">
                  Dashboard
              </Typography>
      

      {/* Summary Cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-5px)' },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {card.title}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {card.value}
                      </Typography>
                    </Box>
                    <Box>{card.icon}</Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Recent Bookings */}
      <Box mt={5}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Recent Bookings
        </Typography>

        {loadingBookings ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : recentBookings.length > 0 ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentBookings.map((booking) => (
                  <TableRow key={booking.booking_number} hover>
                    <TableCell>{booking.booking_number}</TableCell>
                    <TableCell>{booking.contact_person_name}</TableCell>
                    <TableCell>{booking.total_amount}</TableCell>
                    <TableCell>
                      {new Date(booking.booking_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{getStatusChip(booking.status)}</TableCell>
                    <TableCell>
                                        <a className="btn btn-sm btn-icon" href={`/bookings/${booking.booking_id}`}>
                                          <i className="bx bx-show"></i>
                                        </a>
                                      </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No recent bookings found.
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end" mt={2}> <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' } }} onClick={() => window.location.href = '/bookings'} > View All Bookings </Typography> </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
